import { determineDataSource } from "#/Data/DataStores/DataSource.ts";
import { type ShopfrontThunk } from "#/Utilities/Generic/ActionsHelper.ts";
import { deleteOption, getOption, setOption } from "#/Utilities/Platform/Options/Options.ts";
import { POS_FORCE_SYNC } from "#/Workers/Sync/SyncEvents.ts";
import NotFoundException from "../../Routes/SellScreen/Exceptions/Data/NotFoundException";
import AgentManager from "../../Workers/Bases/AgentManager";
import SyncAgent from "../../Workers/Sync/SyncAgent";
import { LOCATION_CHANGE_REGISTER, REGISTER_KICKED, type REQUEST_REGISTER } from "./GeneralActionConstants";
import { type PresenceOverrideAction } from "./PresenceActions";

// Types
type Outlet = {
    id: string;
    name: string;
};

type OutletInput = Outlet | "global";

type Register = {
    id: string;
    name: string;
};

interface ChangeRegisterAction {
    type: typeof LOCATION_CHANGE_REGISTER;
    outlet: null | Outlet;
    register: null | Register;
    global: boolean;
}

interface RequestRegisterAction {
    type: typeof REQUEST_REGISTER;
}

interface KickRegisterAction {
    type: typeof REGISTER_KICKED;
    user: string;
    register: string;
}

export type LocationActionTypes =
    ChangeRegisterAction |
    KickRegisterAction |
    RequestRegisterAction |
    PresenceOverrideAction;

export interface LocationState {
    kicked: false | {
        user: string;
        register: string;
    };
    inUse: boolean;
    outlet: {
        id: string | null;
        name: string | null;
    } | null;
    register: {
        id: string | null;
        name: string | null;
    } | null;
    isGlobal: boolean;
}

// Functions
export function changeRegister(
    outlet?: OutletInput,
    register?: Register,
    performFullSync = true
): LocationActionTypes {
    if(typeof outlet === "undefined" && typeof register === "undefined") {
        performFullSync = false;
    }

    let isGlobal = false;

    if(outlet === "global") {
        isGlobal = true;
        outlet = undefined;
    }

    /*
    These need to be above the POS_FORCE_SYNC because it requires the option
    to be set to not request the location a second time
     */
    setOption("register_kicked", null); // This has to be first
    setOption("global_mode", isGlobal); // This needs to be above the outlet and register
    setOption("outlet", outlet ? outlet.id : "");
    setOption("outlet_name", outlet ? outlet.name : "");
    setOption("register", register ? register.id : "");
    setOption("register_name", register ? register.name : "");
    setOption("previous_register", register ? register.id : getOption("previous_register"));

    if(isGlobal) {
        setOption("register_default_price_set", null);
    }

    // Force the sync
    if(typeof document !== "undefined") {
        // Get the current invoice number from the sync
        deleteOption("register_current_invoice_number");

        setOption("register_open_time", "");
        document.dispatchEvent(new CustomEvent(POS_FORCE_SYNC, {
            detail: {
                full: performFullSync,
            },
        }));
    }

    return {
        type    : LOCATION_CHANGE_REGISTER,
        outlet  : outlet || null,
        register: register || null,
        global  : isGlobal,
    };
}

export function updateRegister(id: string): ShopfrontThunk<LocationActionTypes> {
    return (dispatch): void => {
        if(!id) {
            dispatch(changeRegister());
        } else {
            determineDataSource()
                .then(async source => {
                    const register = await source.registers.get(id)
                        .catch(err => {
                            if(err instanceof NotFoundException) {
                                console.error(
                                    "Could not find the register while updating the location (through updateRegister)"
                                );

                                const syncAgent = AgentManager.getAgent(SyncAgent) as SyncAgent;

                                if(syncAgent.isSyncing()) {
                                    // Wait for a second until retrying
                                    return new Promise(res => {
                                        setTimeout(() => {
                                            res(false);
                                        }, 100);
                                    });
                                }

                                return {
                                    uuid     : id,
                                    outlet_id: "unknown",
                                    name     : "Unknown Register",
                                };
                            }

                            throw err;
                        }) as any;

                    if(register === false) {
                        dispatch(updateRegister(id));

                        return;
                    }

                    const outlet = await source.outlets.get(register.outlet_id)
                        .catch(err => {
                            if(err instanceof NotFoundException) {
                                console.error(
                                    "Could not find the outlet while updating the location (through updateRegister)"
                                );

                                return {
                                    uuid: register.outlet_id,
                                    name: "Unknown Outlet",
                                };
                            }

                            throw err;
                        }) as any;

                    dispatch(changeRegister({
                        id  : outlet.uuid,
                        name: outlet.name,
                    }, {
                        id  : register.uuid,
                        name: register.name,
                    }));
                });
        }
    };
}

export function updateOutlet(id: string): ShopfrontThunk<LocationActionTypes> {
    return (dispatch): void => {
        if(!id) {
            dispatch(changeRegister());
        } else {
            determineDataSource()
                .then(source => {
                    return source.outlets.get(id)
                        .catch(err => {
                            if(err instanceof NotFoundException) {
                                console.error(
                                    "Could not find the outlet while updating the location (through updateLocation)"
                                );

                                return {
                                    uuid: id,
                                    name: "Unknown Outlet",
                                };
                            }

                            throw err;
                        }) as any;
                })
                .then(outlet => {
                    dispatch(changeRegister({
                        id  : outlet.uuid,
                        name: outlet.name,
                    }));
                });
        }
    };
}

export function registerKicked(user: string, register: string): LocationActionTypes {
    setOption("register_kicked", register);
    setOption("previous_register", register);
    setOption("register", null);
    setOption("register_name", "");

    return {
        type: REGISTER_KICKED,
        user,
        register,
    };
}
